import { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Multi-tab workspace state for employee dashboards (see MultiTabWorkspace.jsx).
// Deliberately a LOCAL context - only <MultiTabWorkspace> provides it, so
// any page rendered outside a workspace (e.g. UserDashboard for a
// simple_user, or any page for dispatcher/manager_user) sees `null` from
// useWorkspace()/useWorkspaceNav() and falls back to plain react-router
// navigation, unchanged from before this feature existed.
const WorkspaceContext = createContext(null);

export const MAX_TABS = 10;

// The permanent, non-closeable first tab - always the ticket-list "home"
// view (reuses UserDashboard directly, see MultiTabWorkspace.jsx) so the
// workspace is never empty.
export const HOME_TAB = { id: 'home', path: '/dashboard', title: 'تیکت‌ها', pinned: true };

// Move a tab to the first slot after pinned tabs so recently used tabs stay
// at hand in the RTL tab bar (rightmost / start of the strip).
function bringTabToFront(tabs, id) {
  const index = tabs.findIndex((t) => t.id === id);
  if (index === -1) return tabs;
  const tab = tabs[index];
  if (tab.pinned) return tabs;

  const insertAt = tabs.filter((t) => t.pinned).length;
  if (index === insertAt) return tabs;

  const next = [...tabs];
  next.splice(index, 1);
  next.splice(insertAt, 0, tab);
  return next;
}

export function WorkspaceProvider({ children }) {
  const [tabs, setTabs] = useState([HOME_TAB]);
  const [activeTabId, setActiveTabId] = useState(HOME_TAB.id);

  // Opens `path` as a new tab, or focuses it if already open - this is what
  // keeps switching tabs free of duplicate API calls/remounts (requirement
  // 6): the target page component is mounted exactly once per unique path
  // and just sits there, visible or not, until its tab is closed.
  //
  // New tabs are inserted right after the pinned (home) tab - i.e. at the
  // front of the dynamic tabs - so the newest tab is always the first one
  // shown, not appended at the end.
  const openTab = useCallback((path, title) => {
    setTabs((prev) => {
      const existing = prev.find((t) => t.path === path);
      if (existing) {
        setActiveTabId(existing.id);
        return bringTabToFront(prev, existing.id);
      }

      const newTab = { id: `${path}#${Date.now()}`, path, title, pinned: false };
      const insertAt = prev.filter((t) => t.pinned).length;

      let base = prev;
      if (prev.length >= MAX_TABS) {
        // At the limit: drop the oldest closeable tab to make room, rather
        // than blocking the user or silently dropping the new one. Since
        // new tabs go to the front, "oldest" is now the LAST non-pinned tab.
        let oldestIndex = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (!prev[i].pinned) { oldestIndex = i; break; }
        }
        if (oldestIndex === -1) return prev; // only pinned tabs somehow - no-op
        base = prev.filter((_, i) => i !== oldestIndex);
      }

      const next = [...base];
      next.splice(insertAt, 0, newTab);
      setActiveTabId(newTab.id);
      return next;
    });
  }, []);

  const closeTab = useCallback((id) => {
    setTabs((prev) => {
      const closingIndex = prev.findIndex((t) => t.id === id);
      if (closingIndex === -1 || prev[closingIndex].pinned) return prev;
      const next = prev.filter((t) => t.id !== id);
      setActiveTabId((activeId) => {
        if (activeId !== id) return activeId;
        // Focus whichever tab now sits where the closed one was, falling
        // back a slot if it was the last one.
        const fallback = next[Math.min(closingIndex, next.length - 1)];
        return fallback ? fallback.id : HOME_TAB.id;
      });
      return next;
    });
  }, []);

  const focusTab = useCallback((id) => {
    setActiveTabId(id);
    setTabs((prev) => bringTabToFront(prev, id));
  }, []);

  const value = useMemo(
    () => ({ tabs, activeTabId, openTab, closeTab, focusTab }),
    [tabs, activeTabId, openTab, closeTab, focusTab]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// Returns the workspace's tab state, or null when rendered outside a
// <WorkspaceProvider> (i.e. not inside the multi-tab workspace).
export function useWorkspace() {
  return useContext(WorkspaceContext);
}

// ─── Pane context ───────────────────────────────────────────────────────
// The whole app has exactly one react-router <BrowserRouter> (react-router
// v6 throws if you nest another one), so a workspace pane can't get its own
// isolated useParams()/useNavigate() the way a real route would. Instead,
// WorkspacePane.jsx parses the tab's path itself and provides the resolved
// :id plus a close() callback through this plain React context (context
// propagation doesn't care about router boundaries) - the couple of reused
// pages that need a route param or a "go back" action (TicketProcessPage,
// MissionResponsePage) read this when present and fall back to the real
// useParams()/useNavigate() otherwise, so they work unmodified in their
// normal full-page route too.
const PaneContext = createContext(null);
export const PaneProvider = PaneContext.Provider;
export function usePaneContext() {
  return useContext(PaneContext);
}

// Drop-in replacement for the "navigate to a ticket/mission" click handlers
// scattered across the dashboard list pages. Inside the workspace it opens
// a tab; everywhere else (dispatcher, manager_user, simple_user, or these
// same pages rendered outside the workspace) it's a plain navigate(), so
// existing non-employee behavior is untouched.
export function useWorkspaceNav() {
  const ws = useWorkspace();
  const navigate = useNavigate();
  return useCallback(
    (path, title) => {
      if (ws) ws.openTab(path, title);
      else navigate(path);
    },
    [ws, navigate]
  );
}
